import { type IPrincipleAuthClaims } from '@principle-theorem/principle-core/interfaces';
import { type Observable, combineLatest, from } from 'rxjs';
import { exhaustMap, filter } from 'rxjs/operators';

/**
 * Detects sessions whose auth token predates `orgUserDocIds`, which the
 * Firestore rules need to resolve the user's roles. Each organisation is only
 * attempted once: `setAuthClaims` returns without writing claims for an
 * unverified email, so retrying would loop forever.
 */
export class StaleAuthClaims {
  private _attemptedOrganisationUids = new Set<string>();

  shouldRefresh(
    claims: Partial<IPrincipleAuthClaims> | undefined,
    organisationUid: string | undefined
  ): boolean {
    if (!claims || !organisationUid) {
      return false;
    }
    if (claims.isManagement === true) {
      return false;
    }
    if (claims.orgUserDocIds?.[organisationUid]) {
      return false;
    }
    if (this._attemptedOrganisationUids.has(organisationUid)) {
      return false;
    }
    this._attemptedOrganisationUids.add(organisationUid);
    return true;
  }
}

/**
 * Refreshes the auth claims at most once per organisation for this
 * subscription. A rejected refresh errors the stream so it reaches the global
 * error handler.
 */
export function refreshStaleAuthClaims$(
  claims$: Observable<Partial<IPrincipleAuthClaims> | undefined>,
  organisationUid$: Observable<string | undefined>,
  updateClaims: () => Promise<void>
): Observable<void> {
  const staleAuthClaims = new StaleAuthClaims();
  return combineLatest([claims$, organisationUid$]).pipe(
    filter(([claims, organisationUid]) =>
      staleAuthClaims.shouldRefresh(claims, organisationUid)
    ),
    exhaustMap(() => from(updateClaims()))
  );
}
